import { forwardRef } from "react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Tooltip } from "../atoms/Tooltip";
import { HelpOutlineIcon } from "../lib/icons";
import { cn } from "../lib/cn";

export interface PageHeaderProps
  extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** Page title — the current screen's name. */
  title: ReactNode;
  /** Optional visual before the title block. */
  leading?: ReactNode;
  /** Quiet context beneath the title. */
  subtitle?: ReactNode;
  /** Optional breadcrumb trail above the title — compose from the Breadcrumbs atom. */
  breadcrumbs?: ReactNode;
  /** Right-aligned action nodes (buttons, menus…). */
  actions?: ReactNode;
  /** Standard help action shown beside the title. */
  helpAction?: {
    onClick: () => void;
    testId?: string;
  };
}

/**
 * Page furniture — the recurring page title bar of a portal screen.
 *
 *   <PageHeader
 *     breadcrumbs={<Breadcrumbs>…</Breadcrumbs>}
 *     title="Plot 12 ledger"
 *     actions={<Button size="sm">Add entry</Button>}
 *   />
 *
 * Breadcrumbs (when present) sit on their own quiet row above the title
 * row; the title row keeps actions end-aligned on one baseline.
 */
export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      title,
      leading,
      subtitle,
      breadcrumbs,
      actions,
      helpAction,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          "border-b border-border-default bg-surface-canvas px-4 py-2",
          className,
        )}
      >
        {breadcrumbs ? (
          <div className="mb-1.5 min-w-0 overflow-hidden">{breadcrumbs}</div>
        ) : null}
        <div className="flex min-h-9 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            {leading ? <div className="shrink-0">{leading}</div> : null}
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <h1 className="type-body-lg m-0 min-w-0 truncate font-medium text-content-primary">
                  {title}
                </h1>
                {helpAction ? (
                  <Tooltip content="View help">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6 shrink-0 text-content-secondary hover:text-content-accent"
                      onClick={helpAction.onClick}
                      data-testid={helpAction.testId ?? "help-icon-button"}
                      aria-label="View help"
                    >
                      <HelpOutlineIcon aria-hidden="true" className="size-3" />
                    </Button>
                  </Tooltip>
                ) : null}
              </div>
              {subtitle ? (
                <div className="type-body-sm truncate text-content-secondary">
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>
          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </div>
      </div>
    );
  },
);
PageHeader.displayName = "PageHeader";
